import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Mousetrap from "mousetrap";
import "mousetrap/plugins/global-bind/mousetrap-global-bind";

interface ShortcutContextValue {
  registerShortcut: (
    key: string,
    callback: (e: KeyboardEvent) => void,
    options?: {
      preventDefault?: boolean;
      enabled?: boolean;
      description?: string;
      global?: boolean;
    },
  ) => void;
  unregisterShortcut: (key: string) => void;
}

// Create a context for the provider stack
interface StackContextValue {
  addProvider: (id: string) => void;
  removeProvider: (id: string) => void;
  isTopProvider: (id: string) => boolean;
  getStack: () => string[];
  notifyTopProviderChanged: () => void;
  registerCallback: (id: string, callback: () => void) => void;
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null);
const StackContext = createContext<StackContextValue | null>(null);

export const useShortcuts = () => {
  const context = useContext(ShortcutContext);
  if (!context) {
    throw new Error("useShortcuts must be used within a ShortcutProvider");
  }
  return context;
};

interface ShortcutProviderProps {
  children: React.ReactNode;
}

// Create the root provider that manages the stack
export const ShortcutProviderRoot: React.FC<ShortcutProviderProps> = ({
  children,
}) => {
  const providerStack = useRef<string[]>([]);
  const providerCallbacks = useRef<Map<string, () => void>>(new Map());

  const stackContext: StackContextValue = {
    addProvider: (id: string) => {
      // Unbind shortcuts of the previous top provider before adding the new one
      const prevTopProvider =
        providerStack.current[providerStack.current.length - 1];
      if (prevTopProvider) {
        Mousetrap.reset();
        // Rebind global shortcuts from all providers after reset
        providerCallbacks.current.forEach((callback) => callback());
      }
      providerStack.current = [...providerStack.current, id];
    },
    removeProvider: (id: string) => {
      const wasTop =
        providerStack.current[providerStack.current.length - 1] === id;
      providerStack.current = providerStack.current.filter((pid) => pid !== id);
      providerCallbacks.current.delete(id);

      if (wasTop) {
        // If we removed the top provider, unbind all its shortcuts
        Mousetrap.reset();
        // Rebind global shortcuts from all providers after reset
        providerCallbacks.current.forEach((callback) => callback());

        // Notify the new top provider to bind its shortcuts
        const newTopProvider =
          providerStack.current[providerStack.current.length - 1];
        if (newTopProvider) {
          const callback = providerCallbacks.current.get(newTopProvider);
          if (callback) {
            callback();
          }
        }
      }
    },
    isTopProvider: (id: string) => {
      return providerStack.current[providerStack.current.length - 1] === id;
    },
    getStack: () => {
      return providerStack.current;
    },
    notifyTopProviderChanged: () => {
      const topProvider =
        providerStack.current[providerStack.current.length - 1];
      if (topProvider) {
        const callback = providerCallbacks.current.get(topProvider);
        if (callback) {
          callback();
        }
      }
    },
    registerCallback: (id: string, callback: () => void) => {
      providerCallbacks.current.set(id, callback);
    },
  };

  return (
    <StackContext.Provider value={stackContext}>
      {children}
    </StackContext.Provider>
  );
};

export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({
  children,
}) => {
  const providerId = useRef(Math.random().toString(36).substring(7));
  const stackContext = useContext(StackContext);

  if (!stackContext) {
    throw new Error(
      "ShortcutProvider must be used within a ShortcutProviderRoot",
    );
  }

  // Keep track of registered shortcuts
  const registeredShortcuts = useRef(
    new Map<
      string,
      {
        callback: (e: KeyboardEvent) => void;
        options?: {
          preventDefault?: boolean;
          enabled?: boolean;
          description?: string;
          global?: boolean;
        };
      }
    >(),
  );

  // Helper function to bind shortcuts
  const bindShortcuts = useCallback(() => {
    registeredShortcuts.current.forEach(({ callback, options }, key) => {
      // Global shortcuts should always be bound, regardless of provider hierarchy
      if (options?.global) {
        Mousetrap.bindGlobal(key, (e) => {
          if (options?.preventDefault) {
            e.preventDefault();
          }
          if (options?.enabled !== false) {
            callback(e);
          }
        });
      } else if (stackContext.isTopProvider(providerId.current)) {
        // Non-global shortcuts only bind when this is the top provider
        Mousetrap.bind(key, (e) => {
          if (options?.preventDefault) {
            e.preventDefault();
          }
          if (options?.enabled !== false) {
            callback(e);
          }
        });
      }
    });
  }, [stackContext]);

  // Helper function to unbind shortcuts
  const unbindShortcuts = useCallback(() => {
    registeredShortcuts.current.forEach(({ options }, key) => {
      // Don't unbind global shortcuts - they should persist
      if (!options?.global) {
        if (stackContext.isTopProvider(providerId.current)) {
          Mousetrap.unbind(key);
        }
      }
    });
  }, [stackContext]);

  const registerShortcut = (
    key: string,
    callback: (e: KeyboardEvent) => void,
    options?: {
      preventDefault?: boolean;
      enabled?: boolean;
      description?: string;
      global?: boolean;
    },
  ) => {
    registeredShortcuts.current.set(key, { callback, options });

    // Global shortcuts should always be bound immediately
    if (options?.global) {
      Mousetrap.bindGlobal(key, (e) => {
        if (options?.preventDefault) {
          e.preventDefault();
        }
        if (options?.enabled !== false) {
          callback(e);
        }
      });
    } else if (stackContext.isTopProvider(providerId.current)) {
      // Non-global shortcuts only bind when this is the top provider
      Mousetrap.bind(key, (e) => {
        if (options?.preventDefault) {
          e.preventDefault();
        }
        if (options?.enabled !== false) {
          callback(e);
        }
      });
    }
  };

  const unregisterShortcut = (key: string) => {
    const shortcut = registeredShortcuts.current.get(key);
    if (shortcut) {
      // Always unbind global shortcuts when explicitly unregistered
      if (shortcut.options?.global) {
        Mousetrap.unbind(key);
      } else if (stackContext.isTopProvider(providerId.current)) {
        // Non-global shortcuts only unbind when this is the top provider
        Mousetrap.unbind(key);
      }
    }
    registeredShortcuts.current.delete(key);
  };

  // Handle provider mounting and unmounting
  useEffect(() => {
    const currentProviderId = providerId.current;

    // Register this provider's bind function
    stackContext.registerCallback(currentProviderId, bindShortcuts);
    stackContext.addProvider(currentProviderId);

    // Bind shortcuts if this is the top provider
    bindShortcuts();

    return () => {
      // Unbind all shortcuts if this is the top provider
      unbindShortcuts();

      stackContext.removeProvider(currentProviderId);

      stackContext.notifyTopProviderChanged();
    };
  }, [bindShortcuts, unbindShortcuts, stackContext]);

  return (
    <ShortcutContext.Provider value={{ registerShortcut, unregisterShortcut }}>
      {children}
    </ShortcutContext.Provider>
  );
};

interface ShortcutHandlerProps {
  children: React.ReactNode;
  enabled?: boolean;
}

export const ShortcutHandler: React.FC<ShortcutHandlerProps> = ({
  children,
  enabled = true,
}) => {
  if (!enabled) {
    return <>{children}</>;
  }

  return <ShortcutProvider>{children}</ShortcutProvider>;
};
